/**
 * Single source of truth for handwriting note fonts: DB values, UI labels, and
 * asset file stems (`layers/base/assets/fonts/${stem}.ttf`). Order = display order for presets;
 * the **Custom** tile is listed first in `HANDWRITING_PROJECT_FONT_OPTIONS`.
 */
import {
    DEFAULT_FONT_SIZE_RATIO,
    DEFAULT_LINE_HEIGHT_RATIO,
} from '~~/layers/base/utils/handwrittenNoteTextLayout'

/**
 * `fontSizeRatio` (TK-12497) / `lineHeightRatio` (TK-13401): per-font size and line spacing, overriding
 * the shared {@link DEFAULT_FONT_SIZE_RATIO} / {@link DEFAULT_LINE_HEIGHT_RATIO}.
 *
 * Values confirmed by testing each font in the running app against real handwritten reference cards until
 * the message matched word-for-word. See `scripts/handwriting-font-calibration/` for the reference cards
 * and notes. Re-check by the same method if the reference cards or fonts ever change.
 */
export const HANDWRITING_PROJECT_FONTS = [
    {
        stem: 'beachy_barbara',
        label: 'Beachy Barbara',
        previewTranslateY: '15%',
        fontSizeRatio: 0.03,
        lineHeightRatio: 1.235,
    },
    {
        stem: 'fashionista_finley',
        label: 'Fashionista Finley',
        previewTranslateY: '15%',
        fontSizeRatio: 0.027,
        lineHeightRatio: 1.685,
    },
    {
        stem: 'renaissance_rupert',
        label: 'Renaissance Rupert',
        fontSizeRatio: 0.031,
        lineHeightRatio: 0.96,
    },
] as const

/** Sentinel stored in `handwriting_project.font` when the user selects “+ Custom” (not a loaded TTF). */
export const HANDWRITING_PROJECT_FONT_CUSTOM = 'handwriting_custom' as const

/** Preset stems only (excludes {@link HANDWRITING_PROJECT_FONT_CUSTOM}). */
export type HandwritingProjectPresetFont = (typeof HANDWRITING_PROJECT_FONTS)[number]['stem']

/** Any valid `font` column value: preset stems or the custom sentinel. */
export type HandwritingProjectFont =
    | HandwritingProjectPresetFont
    | typeof HANDWRITING_PROJECT_FONT_CUSTOM

/** Stems in display order (same as `HANDWRITING_PROJECT_FONTS`). */
export const HANDWRITING_PROJECT_FONT_VALUES: readonly HandwritingProjectPresetFont[] =
    HANDWRITING_PROJECT_FONTS.map((f) => f.stem) as readonly HandwritingProjectPresetFont[]

/** Non-empty tuple for Zod `z.enum()` and Drizzle `pgEnum()` for preset stems only. */
export const HANDWRITING_PROJECT_FONT_STEMS = HANDWRITING_PROJECT_FONTS.map((f) => f.stem) as [
    HandwritingProjectPresetFont,
    ...HandwritingProjectPresetFont[],
]

/** All allowed `font` PATCH/GET string values (presets + custom). */
export const HANDWRITING_PROJECT_FONT_ALL_VALUES = [
    ...HANDWRITING_PROJECT_FONT_STEMS,
    HANDWRITING_PROJECT_FONT_CUSTOM,
] as const

export const DEFAULT_HANDWRITING_PROJECT_FONT: HandwritingProjectPresetFont =
    HANDWRITING_PROJECT_FONTS[0].stem

export const HANDWRITING_PROJECT_FONT_OPTIONS: { label: string; value: HandwritingProjectFont }[] =
    [
        { value: HANDWRITING_PROJECT_FONT_CUSTOM, label: 'Custom' },
        ...HANDWRITING_PROJECT_FONTS.map((f) => ({ value: f.stem, label: f.label })),
    ]

/**
 * Resolves a font's `fontSizeRatio` for the message-limit fit-check (TK-12497) — looks up `fontKey` in
 * {@link HANDWRITING_PROJECT_FONTS}, falling back to {@link DEFAULT_FONT_SIZE_RATIO} for
 * {@link HANDWRITING_PROJECT_FONT_CUSTOM} or any unrecognized key. Callers should resolve `fontKey`
 * through `resolveHandwritingFontForFabricPreview` first so this agrees with whichever font family the
 * preview/measurement actually renders in.
 */
export function resolveHandwritingProjectFontSizeRatio(fontKey: string): number {
    const trimmed = fontKey.trim()
    const font = HANDWRITING_PROJECT_FONTS.find((f) => f.stem === trimmed)
    return font?.fontSizeRatio ?? DEFAULT_FONT_SIZE_RATIO
}

/**
 * Resolves a font's `lineHeightRatio` (TK-13401) — same lookup as {@link resolveHandwritingProjectFontSizeRatio},
 * falls back to {@link DEFAULT_LINE_HEIGHT_RATIO}.
 */
export function resolveHandwritingProjectLineHeightRatio(fontKey: string): number {
    const trimmed = fontKey.trim()
    const font = HANDWRITING_PROJECT_FONTS.find((f) => f.stem === trimmed)
    return font?.lineHeightRatio ?? DEFAULT_LINE_HEIGHT_RATIO
}
